import { Clear, FilterAlt } from '@mui/icons-material'
import {
  Autocomplete,
  type AutocompleteRenderInputParams,
  Box,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Popover,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import type {
  GetEmployeesRequest,
  PlaceNode,
} from '@ulysses-inc/harami_api_client'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useDebounce } from 'src/exShared/hooks/useDebounce'
import { splitAccessiblePlaceNode } from 'src/exShared/libraries/filterDropDown/place'
import type { GetTemplatesFilter } from 'src/exShared/types/types'
import type { FlatNode } from 'src/exShared/util/place/flattenNodePath'
import { splitLoginPlaceUUIDs } from 'src/exShared/util/place/splitLoginPlace'
import { getFilter } from 'src/exShared/util/template/getFilter'
import { useListFilter } from 'src/hooks/filter/useListFilter'
import type { TemplatesFilter } from 'src/views/types/utils/ListFilter'

interface OwnProps {
  places: PlaceNode[]
  placeNodes: PlaceNode[]
  updateTemplatesFilter: (filter?: GetTemplatesFilter) => void
  flattenNodePath: FlatNode[]
  isLoadingPlace?: boolean
  templateGetTrigger?: boolean
  request?: GetEmployeesRequest
}

const TemplatesFilterDropDown: React.FC<OwnProps> = ({
  places,
  placeNodes,
  updateTemplatesFilter,
  flattenNodePath,
  isLoadingPlace,
  templateGetTrigger,
  request,
}) => {
  const { filters, setListFilter } = useListFilter()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isOpen = anchorEl !== null
  const [placeNodeIds, setPlaceNodeIds] = useState<string[]>([])
  const [placeGroupNodeIds, setPlaceGroupNodeIds] = useState<string[]>([])
  const [templateName, setTemplateName] = useState<string>('')
  const { loginPlaceGroupIds, loginPlaceIds } = splitLoginPlaceUUIDs()

  const debounce = useDebounce(300)

  const saveFilter = (conditions: TemplatesFilter) => {
    setListFilter({
      ...(filters ?? {}),
      gridVariables: {
        pagination: request,
        filter: conditions,
      },
    })
  }

  const getTemplateFilter = (conditions: TemplatesFilter) => {
    saveFilter(conditions)
    return getFilter(placeNodes, flattenNodePath, conditions)
  }

  const isFilterOn = () => {
    return (
      templateName || placeGroupNodeIds.length > 0 || placeNodeIds.length > 0
    )
  }

  const clearFilter = () => {
    const conditions = {
      templateName: '',
      placeNodeIds: loginPlaceIds,
      placeGroupNodeIds: loginPlaceGroupIds,
    }
    setTemplateName('')
    setPlaceNodeIds([])
    setPlaceGroupNodeIds([])
    saveFilter(conditions)
    const filter = getTemplateFilter(conditions)
    updateTemplatesFilter(filter)
  }

  useEffect(() => {
    const loginPlaceUUIDs = splitLoginPlaceUUIDs()
    // eslint-disable-next-line react-hooks/set-state-in-effect -- useEffect内でのsetState呼び出しは既存設計のため、リファクタリング時に修正する
    setPlaceNodeIds(loginPlaceUUIDs.loginPlaceIds)
    setPlaceGroupNodeIds(loginPlaceUUIDs.loginPlaceGroupIds)
  }, [])

  useEffect(() => {
    if (!templateGetTrigger || isLoadingPlace) {
      return
    }
    if (!filters || !filters.gridVariables) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- useEffect内でのsetState呼び出しは既存設計のため、リファクタリング時に修正する
      setTemplateName('')
      setPlaceNodeIds(loginPlaceIds || [])
      setPlaceGroupNodeIds(loginPlaceGroupIds || [])

      const filter =
        loginPlaceIds.length || loginPlaceGroupIds
          ? getTemplateFilter({
              placeNodeIds: loginPlaceIds,
              placeGroupNodeIds: loginPlaceGroupIds,
            })
          : undefined
      updateTemplatesFilter(filter)
      return
    }
    const gridVariablesFilter = filters.gridVariables.filter
    let filterPlaceNodeIds: string[] = []
    let filterPlaceGroupNodeIds: string[] = []
    if (
      (gridVariablesFilter?.placeNodeIds ?? []).length > 0 ||
      (gridVariablesFilter?.placeGroupNodeIds ?? []).length > 0
    ) {
      filterPlaceNodeIds = gridVariablesFilter?.placeNodeIds ?? []
      filterPlaceGroupNodeIds = gridVariablesFilter?.placeGroupNodeIds ?? []
    } else {
      filterPlaceNodeIds = loginPlaceIds
      filterPlaceGroupNodeIds = loginPlaceGroupIds
    }
    setTemplateName(filters.gridVariables.filter?.templateName ?? '')
    setPlaceNodeIds(filterPlaceNodeIds)
    setPlaceGroupNodeIds(filterPlaceGroupNodeIds)
    const filter = getTemplateFilter({
      templateName: filters.gridVariables.filter?.templateName,
      placeNodeIds: filterPlaceNodeIds,
      placeGroupNodeIds: filterPlaceGroupNodeIds,
    })
    updateTemplatesFilter(filter)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateGetTrigger, placeNodes.length])

  const accessiblePlaceNode = splitAccessiblePlaceNode(placeNodes, places)

  const handleTemplateNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTemplateName(e.target.value)
    debounce(() => {
      const filter = getTemplateFilter({
        templateName: e.target.value,
        placeNodeIds: placeNodeIds,
        placeGroupNodeIds: placeGroupNodeIds,
      })
      updateTemplatesFilter(filter)
    })
  }

  const groundArea = (
    <Button
      color={isFilterOn() ? 'filtered' : 'secondary'}
      variant="outlined"
      startIcon={<FilterAlt />}
      onClick={({ currentTarget }) => {
        setAnchorEl(currentTarget)
      }}
    >
      絞り込み: {isFilterOn() ? 'ON' : 'OFF'}
    </Button>
  )

  const hoverArea = (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="flex-end">
        <Button onClick={clearFilter}>クリア</Button>
      </Stack>
      <Stack>
        <Typography variant="subtitle1">ひな形名</Typography>
        <OutlinedInput
          fullWidth
          size="small"
          onChange={handleTemplateNameChange}
          value={templateName}
          endAdornment={
            templateName && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    setTemplateName('')
                    const filter = getTemplateFilter({
                      templateName: '',
                      placeNodeIds: placeNodeIds,
                      placeGroupNodeIds: placeGroupNodeIds,
                    })
                    updateTemplatesFilter(filter)
                  }}
                  edge="end"
                >
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }
        />
      </Stack>
      <Stack>
        <Typography variant="subtitle1">現場グループ名</Typography>
        <Autocomplete<PlaceNode, true, false, false>
          multiple
          size="small"
          disableCloseOnSelect
          options={accessiblePlaceNode.accessiblePlaceGroups}
          getOptionLabel={option => option.placeGroup?.name ?? '名称未設定'}
          value={placeGroupNodeIds
            .map(uuid =>
              accessiblePlaceNode.accessiblePlaceGroups.find(
                placeNode => placeNode.uuid === uuid,
              ),
            )
            .filter(
              (placeNode): placeNode is PlaceNode => placeNode !== undefined,
            )}
          onChange={(_, newValue) => {
            const newPlaceGroupNodeIds = newValue.map(
              placeNode => placeNode.uuid,
            )
            setPlaceGroupNodeIds(newPlaceGroupNodeIds)
            const filter = getTemplateFilter({
              templateName: templateName,
              placeNodeIds: placeNodeIds,
              placeGroupNodeIds: newPlaceGroupNodeIds,
            })
            updateTemplatesFilter(filter)
          }}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Stack direction="row" alignItems="center" spacing={1} mr={1}>
                <Checkbox size="small" checked={selected} />
                {option.placeGroup?.name}
              </Stack>
            </li>
          )}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <TextField {...params} />
          )}
        />
      </Stack>
      <Stack>
        <Typography variant="subtitle1">現場名</Typography>
        <Autocomplete<PlaceNode, true, false, false>
          multiple
          size="small"
          disableCloseOnSelect
          options={accessiblePlaceNode.accessiblePlaces}
          getOptionLabel={option => option.place?.name ?? '名称未設定'}
          value={placeNodeIds
            .map(uuid =>
              accessiblePlaceNode.accessiblePlaces.find(
                placeNode => placeNode.uuid === uuid,
              ),
            )
            .filter(
              (placeNode): placeNode is PlaceNode => placeNode !== undefined,
            )}
          onChange={(_, newValue) => {
            const newPlaceNodeIds = newValue.map(placeNode => placeNode.uuid)
            setPlaceNodeIds(newPlaceNodeIds)
            const filter = getTemplateFilter({
              templateName: templateName,
              placeNodeIds: newPlaceNodeIds,
              placeGroupNodeIds: placeGroupNodeIds,
            })
            updateTemplatesFilter(filter)
          }}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Stack direction="row" alignItems="center" spacing={1} mr={1}>
                <Checkbox size="small" checked={selected} />
                {option.place?.name}
              </Stack>
            </li>
          )}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <TextField {...params} />
          )}
        />
      </Stack>
    </Stack>
  )

  return (
    <Box>
      {groundArea}
      <Popover
        id="grid-variables-filter-popover"
        anchorEl={anchorEl}
        open={isOpen}
        onClose={() => {
          setAnchorEl(null)
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              padding: 2,
              width: '100%',
              maxWidth: 400,
            },
          },
        }}
      >
        {hoverArea}
      </Popover>
    </Box>
  )
}

export default TemplatesFilterDropDown
